import {
  blobMask,
  HEADER_FINISHES,
  type Header,
  type HeaderShape,
  shapeMask,
} from "@keepoinc/site-page-react-component";
import { CSSProperties, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import Input from "../../../../../../components/Form/Input/input";
import { FormGroup } from "../../../../../../components/Form/styles";
import { ImageField, ImageFieldAction, ImageFieldRow } from "../../../../../../components/ImageField";
import ToggleSwitch from "../../../../../../components/ToggleSwitch";
import { CropProps } from "../../../../../../components/UploadImageInput";
import { Theme } from "../../../../../../model/theme";
import { photoBottomColor } from "../../../../../../utils/photoColor";
import { classNames } from "../../../../../../utils/styles";
import { Divider, fieldLabel } from "../../styles";
import { AlignOptionContainer, AlignOptionsContainer } from "../AlignSection/styles";
import { KindOptions, KindsContainer } from "../BackgroundKinds";
import { BackgroundMediaModal } from "../BackgroundMediaModal";
import { FieldPair } from "../ColorField";
import { FRAMES, type Frame, SamplePicker } from "./SamplePicker";

/** The top's styles, in the order they're offered (all but classic are PLUS). */
const HEADER_STYLES = ["classic", "hero", "banner", "seal", "filled", "magazine"] as const;

/** Around the photo (classic and banner), in the buttons' color: none, a ring or a shadow to the side. */
const FINISHES = ["none", ...HEADER_FINISHES] as const;

/** Where the banner's photo and name go: left (none) is the default. */
const BANNER_ALIGNS = [
  ["left", "LEFT"],
  ["center", "CENTER"],
  ["right", "RIGHT"],
] as const;
type HeaderStyle = (typeof HEADER_STYLES)[number];

/** The photo: the Welcome's (the same image), 400 x 400. */
const PHOTO_CROP: CropProps = { slice: { aspect: 1 }, size: { width: 400, height: 400 }, cropShape: "round" };

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  /* the image blocks as light as the other fields (as Fundo's), their hint readable and their line showing */
  .image-block > div {
    background: var(--color-neutral-base);

    .hint:not(.error) {
      color: #a3a8ba;
    }

    > div:last-child {
      border-top-color: rgba(255, 255, 255, 0.12);

      > * + * {
        border-left-color: rgba(255, 255, 255, 0.12);
      }
    }
  }
`;

/* Destaque's "match the background to the photo": as the gradient's "Animar" — its name and hint, a switch on the right */
const MatchToggle = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  font-family: Inter, sans-serif;

  > span {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .label {
    ${fieldLabel}
  }

  .hint {
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-white);
    opacity: 0.7;

    &.error {
      color: #ffb066;
      opacity: 1;
    }
  }

  label {
    height: 24px;
    margin-bottom: 0;
  }
`;

/* A small drawing of the top in each card: the page's color, the name as a bar, the photo where it goes */
const Mock = styled.span`
  position: absolute;
  inset: 0;

  > span {
    position: absolute;
    background-position: center;
    background-size: cover;
  }

  .bar {
    height: 10%;
    border-radius: 999px;
    opacity: 0.85;
  }

  .dot {
    aspect-ratio: 1;
    border-radius: 50%;
  }
`;

const cover = (url?: string): CSSProperties =>
  url ? { backgroundImage: `url("${url.replace(/"/g, "%22")}")` } : { backgroundColor: "#c9c9d1" };

type Props = {
  header?: Header;
  onChange: (header?: Header) => void;
  /** The Welcome's photo: the top's image in every style. */
  photo?: string;
  /** The Keepo has a Welcome section (where the photo is kept); without one there's no photo to choose. Default: yes. */
  hasWelcome?: boolean;
  /** The Keepo's name (the Welcome's): the seal's words when it has none of its own. */
  name?: string;
  /** The Keepo's: the cover's files go to its folder. */
  siteId: string;
  /** What Pexels opens searched for (the Keepo's field of work). */
  pexelsSearch?: string;
  /** Destaque: the page's color made the photo's (its bottom's), the background a plain color under it. */
  onMatchColor?: (color: string) => void;
  /** Destaque: the page's colors as they were before matching them to the photo. */
  onUnmatch?: () => void;
  /** The page's color is the photo's (the switch is on). */
  matched?: boolean;
  onPhoto: (photo?: string) => void;
  /** The Keepo's colors, for the cards' drawings. */
  theme: Theme;
  /** PLUS only (all but classic): asks to subscribe (`onLocked`) instead. */
  locked?: boolean;
  onLocked?: () => void;
};

/** The top's own words, at most (the API's limits too): the seal's, the filled letters' and the magazine's. */
const WORDS_MAX = { sealText: 30, filledText: 20, magazineText: 30 } as const;

/** Which of them each style writes. */
const WORDS_KEY = { seal: "sealText", filled: "filledText", magazine: "magazineText" } as const;

/** "Topo": the Keepo's photo, how its top looks (one of six styles) and what each style has. */
export const HeaderFields = ({
  header,
  onChange,
  photo,
  hasWelcome = true,
  name,
  onPhoto,
  onMatchColor,
  onUnmatch,
  siteId,
  pexelsSearch,
  matched,
  theme,
  locked,
  onLocked,
}: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.settings.groups.headerFields";
  const style: HeaderStyle = header?.style ?? "classic";
  const frame = header?.shape ?? "round";
  const bannerAlign = header?.align ?? "left";
  const finish = header?.finish ?? "none";
  // Capa: the modal to add or change its image or video (one's own, or from Pexels)
  const [pickingCover, setPickingCover] = useState(false);
  const coverThumb = header?.bannerVideo ? header.bannerPoster : header?.banner;
  const hasCover = !!header?.banner || !!header?.bannerVideo;
  // Capa and Revista: both over the cover's image, the photo and the name placed as it says
  const coverStyle = style === "banner" || style === "magazine" ? style : undefined;

  // Destaque's "match the background": reading the photo, or it couldn't be read
  const [matching, setMatching] = useState<"reading" | "failed">();
  const matchColor = async (on: boolean) => {
    if (!on) {
      setMatching(undefined);
      onUnmatch?.();
      return;
    }
    if (!photo || matching === "reading") return;
    setMatching("reading");
    try {
      onMatchColor?.(await photoBottomColor(photo));
      setMatching(undefined);
    } catch {
      setMatching("failed");
    }
  };
  // the seal's and the filled letters' words: their own, or the name to start with (to be changed)
  const words = (key: keyof typeof WORDS_MAX) => header?.[key] ?? (name ?? "").slice(0, WORDS_MAX[key]);
  const bar = { backgroundColor: theme.secondary };

  // anything but the classic, round top with no finish is PLUS: without it, a change of the look asks to subscribe
  // (going back to the classic, or the words of a top already chosen, don't)
  const change = (next: Header) => {
    const plusLook = next.style !== "classic" || next.shape || next.finish;
    const lookChanged = next.style !== style || next.shape !== header?.shape || next.finish !== header?.finish;
    if (locked && plusLook && lookChanged) {
      onLocked?.();
      return;
    }
    // the banner, the frame, the alignment and the finish stay while switching (the API keeps what the style uses)
    const {
      style: nextStyle,
      banner,
      bannerVideo,
      bannerPoster,
      shape,
      align,
      finish: nextFinish,
      sealText,
      filledText,
      magazineText,
    } = next;
    onChange(
      nextStyle === "classic" && !shape && !banner && !bannerVideo && !nextFinish
        ? undefined
        : {
            style: nextStyle,
            ...(banner && { banner }),
            ...(bannerVideo && { bannerVideo }),
            ...(bannerPoster && { bannerPoster }),
            ...(shape && { shape }),
            ...(align && { align }),
            ...(nextFinish && { finish: nextFinish }),
            // emptied while editing stays empty (the Keepo then shows the name)
            ...(sealText !== undefined && { sealText }),
            ...(filledText !== undefined && { filledText }),
            ...(magazineText !== undefined && { magazineText }),
          }
    );
  };

  // the photo as the Keepo draws it: in the classic's frame (the banner's is round), with a finish behind it
  const finishSample = (
    each: (typeof FINISHES)[number],
    place: CSSProperties = { width: "52%" },
    classic = style === "classic"
  ) => {
    const mask = classic && header?.shape ? shapeMask(header.shape) : undefined;
    const text = theme.titleColor ?? theme.secondary;
    const base = { position: "absolute", inset: 0 } as const;
    const layer = { ...base, ...(mask ? { mask, WebkitMask: mask } : { borderRadius: "50%" }) } as const;
    const paint = (background: string, transform = "none", extra: CSSProperties = {}) => (
      <span style={{ ...layer, background, transform, ...extra }} />
    );
    const ring = (background: string) => (
      <>
        {paint(background, "scale(1.16)")}
        {paint(theme.primary, "scale(1.08)")}
      </>
    );
    // as the Keepo draws each finish behind the photo (a filter goes on a layer around the shape)
    const behind: Record<(typeof FINISHES)[number], ReactNode> = {
      none: null,
      ring: ring(theme.buttons),
      gradient: ring(`linear-gradient(135deg, ${theme.buttons}, ${text})`),
      shadow: paint(theme.buttons, "translate(8%, 8%)"),
      layers: (
        <>
          {paint(text, "translate(14%, 14%)")}
          {paint(theme.buttons, "translate(7%, 7%)")}
        </>
      ),
      glow: <span style={{ ...base, filter: "blur(4px)", opacity: 0.75 }}>{paint(theme.buttons, "scale(1.14)")}</span>,
      sticker: (
        <span style={{ ...base, filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3))" }}>
          {paint("#fff", "scale(1.14)")}
        </span>
      ),
      softShadow: (
        <span style={{ ...base, filter: "drop-shadow(0 4px 4px rgba(0, 0, 0, 0.3))" }}>{paint(theme.primary)}</span>
      ),
      blob: (
        <span
          style={{
            ...base,
            mask: blobMask(),
            WebkitMask: blobMask(),
            background: theme.buttons,
            opacity: 0.35,
            transform: "translate(16%, -8%) scale(1.3)",
          }}
        />
      ),
      frame: (
        <>
          {paint(theme.buttons, "scale(1.24)")}
          {paint("#fff", "scale(1.21)")}
        </>
      ),
    };
    return (
      <span style={{ position: "relative", aspectRatio: "1", ...place }}>
        {behind[each]}
        <span style={{ ...layer, ...cover(photo), backgroundSize: "cover", backgroundPosition: "center" }} />
      </span>
    );
  };

  // the photo cut in a frame, for the Formato's samples
  const frameSample = (each: Frame) => (
    <span
      style={{
        display: "block",
        width: "78%",
        aspectRatio: "1",
        ...cover(photo),
        backgroundSize: "cover",
        backgroundPosition: "center",
        ...(each === "round" ? { borderRadius: "50%" } : { mask: shapeMask(each), WebkitMask: shapeMask(each) }),
      }}
    />
  );

  const finishPicker = (
    <SamplePicker
      label={t(`${prefix}.finish`)}
      options={FINISHES}
      value={finish}
      name={each => t(`${prefix}.finishes.${each}`)}
      draw={each => finishSample(each, { width: "62%" })}
      pageColor={theme.primary}
      onPick={each => change({ ...header, style, finish: each === "none" ? undefined : each })}
    />
  );
  // without PLUS, the classic is the round one with no finish (the free one)
  const pick = (next: HeaderStyle) =>
    next !== style && change(locked && next === "classic" ? { style: next } : { ...header, style: next });

  const drawing = (kind: HeaderStyle) => {
    switch (kind) {
      case "classic":
        return (
          <Mock>
            <span className="bar" style={{ ...bar, left: "12%", top: "45%", width: "42%" }} />
            {/* with its frame and finish, as chosen */}
            {finishSample(
              finish,
              {
                position: "absolute",
                right: "12%",
                top: "28%",
                width: "30%",
              },
              true
            )}
          </Mock>
        );
      case "hero":
        return (
          <Mock>
            <span
              style={{
                ...cover(photo),
                inset: "0 0 30% 0",
                maskImage: "linear-gradient(to bottom, #000 40%, transparent 80%)",
                WebkitMaskImage: "linear-gradient(to bottom, #000 40%, transparent 80%)",
              }}
            />
            <span className="bar" style={{ ...bar, left: "25%", top: "72%", width: "50%" }} />
          </Mock>
        );
      case "banner":
        return (
          <Mock>
            <span
              style={{
                ...cover(header?.banner ?? header?.bannerPoster),
                inset: "0 0 62% 0",
                opacity: header?.banner || header?.bannerPoster ? 1 : 0.6,
              }}
            />
            <span className="dot" style={{ ...cover(photo), left: "35%", top: "20%", width: "30%" }} />
            <span className="bar" style={{ ...bar, left: "25%", top: "76%", width: "50%" }} />
          </Mock>
        );
      case "magazine":
        // the cover's image across the top, darkened at its foot, the name big over it and the photo as a badge
        return (
          <Mock>
            <span
              style={{
                ...cover(header?.banner ?? header?.bannerPoster),
                inset: "0 0 28% 0",
                opacity: header?.banner || header?.bannerPoster ? 1 : 0.6,
              }}
            />
            <span style={{ inset: "0 0 28% 0", background: "linear-gradient(transparent 30%, rgba(0, 0, 0, 0.55))" }} />
            <span className="dot" style={{ ...cover(photo), left: "8%", top: "8%", width: "22%" }} />
            <span className="bar" style={{ backgroundColor: "#fff", left: "8%", top: "44%", width: "52%" }} />
            <span className="bar" style={{ backgroundColor: "#fff", left: "8%", top: "57%", width: "34%" }} />
          </Mock>
        );
      case "seal":
        // centered in the 4:3 card: a width of W is 4/3 of it in height
        return (
          <Mock>
            <span
              style={{
                ...cover(photo),
                left: "33%",
                top: "27.3%",
                width: "34%",
                aspectRatio: "1",
                borderRadius: "50%",
              }}
            />
            <span
              style={{
                left: "22%",
                top: "12.7%",
                width: "56%",
                aspectRatio: "1",
                borderRadius: "50%",
                border: `3px dotted ${theme.secondary}`,
                opacity: 0.85,
              }}
            />
          </Mock>
        );
      case "filled":
        return (
          <Mock>
            <span
              style={{
                ...cover(photo),
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Poppins, sans-serif",
                fontWeight: 700,
                fontSize: "2.1rem",
                letterSpacing: "-0.04em",
                color: "transparent",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                backgroundColor: photo ? undefined : theme.secondary,
              }}>
              AB
            </span>
          </Mock>
        );
    }
  };

  return (
    <Fields>
      {/* the photo first: every style has it */}
      <div className="image-block">
        {hasWelcome ? (
          <ImageField
            shape="round"
            crop={PHOTO_CROP}
            title={t(`${prefix}.photo`)}
            hint={t(`${prefix}.photoHint`)}
            image={photo}
            onChange={url => onPhoto(url)}
          />
        ) : (
          <p role="note" style={{ margin: 0, fontSize: "0.875rem", color: "#a3a8ba" }}>
            {t(`${prefix}.photoNoWelcome`)}
          </p>
        )}
      </div>
      <Divider />
      <KindsContainer>
        <span className="label" aria-hidden="true">
          {t(`${prefix}.style`)}
        </span>
        <KindOptions role="radiogroup" aria-label={t(`${prefix}.style`)}>
          {HEADER_STYLES.map(kind => (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={style === kind}
              className={classNames({ selected: style === kind })}
              onClick={() => pick(kind)}>
              <span className="frame" aria-hidden="true">
                <span className="thumbnail" style={{ backgroundColor: theme.primary }}>
                  {drawing(kind)}
                </span>
              </span>
              {t(`${prefix}.styles.${kind}`)}
            </button>
          ))}
        </KindOptions>
      </KindsContainer>

      {/* the banner first (it's on top), then the photo over its edge */}
      {/* the cover: an image or a video (one's own or from Pexels), in the editor's image block */}
      {coverStyle && (
        <>
          <Divider />
          <div className="image-block">
            <ImageFieldRow
              shape="wide"
              thumb={coverThumb ? <img src={coverThumb} alt="" /> : undefined}
              title={t(`${prefix}.banner`)}
              hint={t(
                `${prefix}.${header?.bannerVideo ? "bannerVideoHint" : coverStyle === "magazine" ? "magazineHint" : "bannerHint"}`
              )}
              wrapThumb={box => (
                <span role="presentation" onClick={() => setPickingCover(true)}>
                  {box}
                </span>
              )}
              actions={
                <>
                  <ImageFieldAction
                    type="button"
                    $tone={hasCover ? "default" : "primary"}
                    onClick={() => setPickingCover(true)}>
                    {t(`${prefix}.${hasCover ? "coverChange" : "coverAdd"}`)}
                  </ImageFieldAction>
                  {hasCover && (
                    <ImageFieldAction
                      type="button"
                      $tone="danger"
                      onClick={() =>
                        change({
                          ...header,
                          style: coverStyle,
                          banner: undefined,
                          bannerVideo: undefined,
                          bannerPoster: undefined,
                        })
                      }>
                      {t("components.imageField.remove")}
                    </ImageFieldAction>
                  )}
                </>
              }
            />
          </div>
          {pickingCover && (
            <BackgroundMediaModal
              cover
              magazine={coverStyle === "magazine"}
              kind="image"
              siteId={siteId}
              search={pexelsSearch}
              onExit={() => setPickingCover(false)}
              onDone={({ url, poster, type }) => {
                setPickingCover(false);
                change({
                  ...header,
                  style: coverStyle,
                  ...(type === "video"
                    ? { banner: undefined, bannerVideo: url, bannerPoster: poster }
                    : { banner: url, bannerVideo: undefined, bannerPoster: undefined }),
                });
              }}
            />
          )}
        </>
      )}

      {coverStyle && (
        <>
          <Divider />
          <KindsContainer>
            <span className="label" aria-hidden="true">
              {t(`${prefix}.bannerAlign`)}
            </span>
            <AlignOptionsContainer role="radiogroup" aria-label={t(`${prefix}.bannerAlign`)}>
              {BANNER_ALIGNS.map(([align, drawn]) => (
                <AlignOptionContainer
                  key={align}
                  type="button"
                  role="radio"
                  aria-checked={bannerAlign === align}
                  aria-label={t(`${prefix}.aligns.${align}`)}
                  $active={bannerAlign === align}
                  $align={drawn}
                  onClick={() => change({ ...header, style: coverStyle, align: align === "left" ? undefined : align })}>
                  <span />
                  <span />
                  <span />
                </AlignOptionContainer>
              ))}
            </AlignOptionsContainer>
          </KindsContainer>
        </>
      )}

      {/* Destaque: the photo fades into the page; its color can be made the photo's own */}
      {style === "hero" && photo && onMatchColor && (
        <>
          <Divider />
          <MatchToggle>
            <span>
              <span className="label">{t(`${prefix}.match.title`)}</span>
              <span className={classNames("hint", { error: matching === "failed" })}>
                {t(`${prefix}.match.${matching === "failed" ? "error" : "text"}`)}
              </span>
            </span>
            <ToggleSwitch label={t(`${prefix}.match.title`)} value={!!matched} setValue={matchColor} />
          </MatchToggle>
        </>
      )}

      {/* the seal's, the filled letters' and the magazine's words: their own, or the name (the editor's text field) */}
      {(style === "seal" || style === "filled" || style === "magazine") && (
        <>
          <Divider />
          {/* half a row, as the Formato */}
          <FieldPair>
            <FormGroup>
              <Input
                id={WORDS_KEY[style]}
                type="text"
                label={t(`${prefix}.${WORDS_KEY[style]}`)}
                value={words(WORDS_KEY[style])}
                maxLength={WORDS_MAX[WORDS_KEY[style]]}
                onChange={event => change({ ...header, style, [WORDS_KEY[style]]: event.target.value })}
              />
            </FormGroup>
          </FieldPair>
        </>
      )}

      {/* the banner's photo finish, half a row */}
      {style === "banner" && (
        <>
          <Divider />
          <FieldPair>{finishPicker}</FieldPair>
        </>
      )}

      {/* the classic photo's frame and its finish, side by side (as the Grafismo's Desenho) */}
      {style === "classic" && (
        <>
          <Divider />
          <FieldPair>
            <SamplePicker
              label={t(`${prefix}.shape`)}
              options={FRAMES}
              value={frame}
              name={each => t(`${prefix}.shapes.${each}`)}
              draw={frameSample}
              pageColor={theme.primary}
              onPick={each =>
                change({ ...header, style: "classic", shape: each === "round" ? undefined : (each as HeaderShape) })
              }
            />
            {finishPicker}
          </FieldPair>
        </>
      )}
    </Fields>
  );
};
